import { useCallback, useMemo, useState } from 'react';
import { EmptyState } from '@carboledger/ui';
import { SummaryTiles } from '@/components/common';
import { useTranslations } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';

/**
 * Props:
 *   header      the sub-tab strip drawn inside the card
 *   useStrip    changes with the sub-tab, so callers key this component on it
 *   filters     the filters narrowing this list, or null when its input has no key for the facility
 *   stripFilters  the strip's own filters when they differ from the list's; defaults to `filters`
 *   stripArgs   extra variables the strip takes beyond `filters`
 *   buildTiles  (t, strip) => Array<{ label, value }>
 *   children    (onEmptyChange) => the filtered table
 */
export const InventoryListTab = ({
  header,
  filters,
  stripFilters,
  useStrip,
  stripArgs,
  buildTiles,
  children,
}) => {
  const t = useTranslations();
  // Figma draws no tiles above an empty tab, so the table reports when it is genuinely empty.
  const [isEmpty, setIsEmpty] = useState(false);
  const handleEmptyChange = useCallback((empty) => setIsEmpty(empty), []);

  const { data: strip } = useStrip(
    { filters: stripFilters ?? filters, ...stripArgs },
    { skip: !filters },
  );

  const tiles = useMemo(
    () => (filters && !isEmpty ? buildTiles(t, strip) : EMPTY_LIST),
    [filters, isEmpty, buildTiles, t, strip],
  );

  // Never render an unfiltered table: another facility's rows would read as this material's.
  if (!filters) {
    return (
      <SummaryTiles header={header}>
        <EmptyState
          image={<img src={emptyBoxIllustration} alt="" width={200} height={200} />}
          title={t('INVENTORY_UNAVAILABLE_TITLE')}
          description={t('INVENTORY_UNAVAILABLE_DESCRIPTION')}
        />
      </SummaryTiles>
    );
  }

  return (
    <SummaryTiles header={header} tiles={tiles}>
      {children(handleEmptyChange)}
    </SummaryTiles>
  );
};
